import type { PostHog } from 'posthog-js'

// Activation-funnel events tracked in PostHog. Keep these names stable — the
// PostHog insights (activation funnel + solo-entry retention) are built on the
// literal strings. Changing a value here orphans the existing insight.
export const AnalyticsEvent = {
    SignedUp: 'user_signed_up',
    WorkspaceCreated: 'workspace_created',
    ProjectCreated: 'project_created',
    TaskCreated: 'task_created',
    InviteSent: 'invite_sent',
    InviteAccepted: 'invite_accepted',
    // Paywall funnel: limit_hit → upgrade_clicked → checkout_started (server) →
    // checkout_completed → plan_purchased (server, authoritative — checkout_completed
    // only means the Stripe success redirect arrived).
    LimitHit: 'limit_hit',
    UpgradeClicked: 'upgrade_clicked',
    CheckoutCompleted: 'checkout_completed',
    // Activation depth beyond task_created.
    TaskCompleted: 'task_completed',
    SharePublished: 'share_published',
    ApiKeyCreated: 'api_key_created',
} as const

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent]

// PostHog is only initialized when VITE_POSTHOG_KEY is set (production). In dev
// the key is absent, so every helper below no-ops instead of loading the SDK.
const enabled = Boolean(import.meta.env.VITE_POSTHOG_KEY)

// PostHog is loaded lazily (dynamic import) so the ~210 KB SDK stays out of the
// eager bundle and its init never runs synchronously before the first React
// render. The first call to any helper (or warmAnalytics on idle) triggers the
// load; helpers queue onto the same promise so an early identifyUser during the
// auth bootstrap is not lost — it just fires once the client has loaded.
let clientPromise: Promise<PostHog | null> | null = null

function ensureClient(): Promise<PostHog | null> {
    if (!enabled) return Promise.resolve(null)
    if (!clientPromise) {
        clientPromise = import('posthog-js')
            .then((mod) => {
                mod.default.init(import.meta.env.VITE_POSTHOG_KEY, { api_host: 'https://eu.i.posthog.com' })
                return mod.default
            })
            .catch((e) => {
                console.error('[analytics] load failed', e)
                return null
            })
    }
    return clientPromise
}

function withClient(label: string, fn: (ph: PostHog) => void): void {
    if (!enabled) return
    void ensureClient().then((ph) => {
        if (!ph) return
        try {
            fn(ph)
        } catch (e) {
            console.error(`[analytics] ${label} failed`, e)
        }
    })
}

/** Kick off the PostHog load on idle so pageview autocapture starts soon after first paint. */
export function warmAnalytics(): void {
    if (!enabled) return
    void ensureClient()
}

/** Tie subsequent events to a stable user id (required for D1/D7 retention). */
export function identifyUser(userId: string, props?: Record<string, unknown>): void {
    withClient('identify', (ph) => ph.identify(userId, props))
}

/** Drop the identified user on logout so the next session starts anonymous. */
export function resetUser(): void {
    withClient('reset', (ph) => ph.reset())
}

export function trackEvent(event: AnalyticsEventName, props?: Record<string, unknown>): void {
    withClient('capture', (ph) => ph.capture(event, props))
}

/** Set durable person properties (e.g. invited_teammate) for cohort/retention splits. */
export function setPersonProps(props: Record<string, unknown>): void {
    withClient('setPersonProperties', (ph) => ph.setPersonProperties(props))
}

// "Just signed up" flag — set the moment an account is created (email or Google),
// consumed on the first authenticated load to tell whether the new user landed
// on an invited (non-owner) workspace, i.e. accepted an invite. sessionStorage
// survives the Google OAuth redirect round-trip (same origin, same tab).
const SIGNUP_FLAG = 'plate_just_signed_up'

export function markJustSignedUp(): void {
    try {
        sessionStorage.setItem(SIGNUP_FLAG, '1')
    } catch {
        // ignore — invite_accepted is best-effort
    }
}

export function consumeJustSignedUp(): boolean {
    try {
        const present = sessionStorage.getItem(SIGNUP_FLAG) === '1'
        if (present) sessionStorage.removeItem(SIGNUP_FLAG)
        return present
    } catch {
        return false
    }
}

export function clearJustSignedUp(): void {
    try {
        sessionStorage.removeItem(SIGNUP_FLAG)
    } catch {
        // ignore
    }
}
